import { Typography, Box, Card, CardContent, Alert, Link, Tooltip } from "@mui/material";
import { Science as ScienceIcon } from "@mui/icons-material";
import { LAB_ICON_MAP } from "../config/lab-icons.config";

/**
 * Getting Started guide for creating Insight labs.
 */
export function GettingStartedPage() {
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <ScienceIcon sx={{ fontSize: 40 }} color="primary" />
        <Typography variant="h4">Creating an Insight Lab</Typography>
      </Box>

      <Typography variant="body1" color="text.secondary" paragraph>
        Follow these steps to create a new experimental lab in the Insight Labs environment.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Step 1: Create a New GitLab Project
          </Typography>
          <Typography variant="body2" paragraph>
            Create a new git project in the{" "}
            <Link
              href="https://gitlab.com/tveyes/internal/twosion/insight-labs"
              target="_blank"
              rel="noopener noreferrer"
            >
              insight-labs GitLab group
            </Link>
            .
          </Typography>
          <Typography variant="body2">
            Choose to start from the group template:{" "}
            <Link
              href="https://gitlab.com/tveyes/internal/templates/toolassisteddevtemplate"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontFamily: "monospace" }}
            >
              toolassisteddevtemplate
            </Link>
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Step 2: Clone and Bootstrap
          </Typography>
          <Typography variant="body2" paragraph>
            Once the repository is created, clone it locally:
          </Typography>
          <Box
            component="code"
            sx={{
              display: "block",
              p: 2,
              bgcolor: "background.default",
              borderRadius: 1,
              mb: 2,
              fontFamily: "monospace",
            }}
          >
            git clone [your-repo-url]
          </Box>
          <Typography variant="body2" paragraph>
            In WSL, run the bootstrap script:
          </Typography>
          <Box
            component="code"
            sx={{
              display: "block",
              p: 2,
              bgcolor: "background.default",
              borderRadius: 1,
              fontFamily: "monospace",
            }}
          >
            ./bootstrap.sh
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Step 3: Configure Your Lab
          </Typography>
          <Typography variant="body2" paragraph>
            Answer the bootstrap prompts:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Project type:</strong> Choose <code>javascript</code>
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Name:</strong> Use the pattern <code>labs-my-lab-name</code>
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Application type:</strong> Choose <code>feature</code> (not application)
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Auth0:</strong> Choose <code>yes</code> to enable Auth0
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Remaining prompts:</strong> Answer <code>yes</code> to clean up template files
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Step 4: Develop Your Lab
          </Typography>
          <Typography variant="body2" paragraph>
            Build your experimental feature using the generated template. The template includes:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              Insight-matching theme and styling
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              React Router for navigation
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              Auth0 integration for authentication
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              TypeScript, testing, and code quality tools
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Step 5: Integration
          </Typography>
          <Typography variant="body2" paragraph>
            When ready to integrate your lab into this environment, you'll need to:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              Create Kubernetes manifests (ConfigMap, Deployment, Service)
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              Add your lab to the Ingress configuration
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              Update the labs.json configuration in the host ConfigMap
            </Typography>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              See existing lab configurations in <code>kubernetes/twosion-dev/</code> for examples.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Step 6: Access Control
          </Typography>
          <Typography variant="body2" paragraph>
            You can restrict which users see your lab in the navigation by adding a{" "}
            <code>require_claim</code> field to your lab entry in <code>labs.json</code>. When set,
            only users whose access token includes that claim value will see the lab.
          </Typography>
          <Box
            component="code"
            sx={{
              display: "block",
              p: 2,
              bgcolor: "background.default",
              borderRadius: 1,
              mb: 2,
              fontFamily: "monospace",
              whiteSpace: "pre",
            }}
          >
            {`{
  "id": "my-lab",
  "require_claim": "my-lab-access",
  "access_denied": "disabled"
}`}
          </Box>
          <Typography variant="body2" paragraph>
            The <code>access_denied</code> field controls what happens when a user lacks the
            required claim:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <code>&quot;hidden&quot;</code> (default) — The lab is completely hidden from the
              navigation.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <code>&quot;disabled&quot;</code> — The lab appears in the navigation but is greyed
              out with a tooltip indicating access is required.
            </Typography>
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              This controls <strong>navigation visibility only</strong>. It does not secure the lab
              itself. Each lab is responsible for enforcing its own authentication and
              authorization. A hidden or disabled nav item does not prevent direct access to the
              lab&apos;s URL.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Available Icons
          </Typography>
          <Typography variant="body2" paragraph>
            Use the <code>icon</code> field in your lab config to set a nav icon. Available names:
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              maxHeight: 400,
              overflow: "auto",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              p: 1,
            }}
          >
            {Object.entries(LAB_ICON_MAP).map(([name, Icon]) => (
              <Tooltip key={name} title={name} placement="top">
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.5,
                    p: 1.5,
                    borderRadius: 1,
                    border: 1,
                    borderColor: "divider",
                    minWidth: 80,
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Icon />
                  <Typography variant="caption" noWrap>
                    {name}
                  </Typography>
                </Box>
              </Tooltip>
            ))}
          </Box>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Need a different icon? Browse the full MUI icon library at{" "}
            <Link
              href="https://mui.com/material-ui/material-icons/"
              target="_blank"
              rel="noopener noreferrer"
            >
              mui.com/material-ui/material-icons
            </Link>{" "}
            and request it to be added to the icon map.
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Learn More
          </Typography>
          <Typography variant="body2">
            For more information about the template system, see the{" "}
            <Link
              href="https://gitlab.com/tveyes/internal/templates/toolassisteddevtemplate"
              target="_blank"
              rel="noopener noreferrer"
            >
              toolassisteddevtemplate repository
            </Link>
            .
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
